/* @jsx h */
import { api } from "@app/data/api";
import { SegmentSelect } from "@app/vue/components/UIComponents";
import { FloorPlan, FloorPlanTable, Order, TableStatus } from "@lemmonhq/types";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { defineComponent, onMounted, ref, Ref, h } from "vue";
import { TableOverview } from "./TableOverview";
import { TablePaymentsList } from "./TablePaymentsList";
import { TableSettings } from "./TableSettings";
import { TableToolbar } from "./TableToolbar";
import { TableNotesModal } from "@app/vue/pages/Tables/TableNotesModal";
import { ChangeTableModal } from "@app/vue/pages/Tables/ChangeTableModal";
import { TransferItemsToTableModal } from "@app/vue/pages/Tables/TransferItemsToTableModal";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { useMutation } from "@tanstack/vue-query";

export const TableSheet = defineComponent<{
  activeTable: Ref<FloorPlanTable | undefined>;
  tables: TableStatus[];
  onPayment?: Function;
  floorPlan?: FloorPlan;
  onFloorPlanChange: (fp: FloorPlan) => void;
  isEditable?: boolean;
}>({
  name: "TableSheet",
  props: [
    "activeTable",
    "tables",
    "onPayment",
    "floorPlan",
    "onFloorPlanChange",
    "isEditable",
  ],
  setup(props) {
    const tableOrders = ref<Order[]>([]);
    const isLoadingOrders = ref(false);
    const showChangeTableModal = ref(false);
    const showReassignItemsModal = ref(false);
    const tableStatus = props.tables.find(
      (el) => el.id === props.activeTable.value?.tableId,
    );
    const fetchTableOrders = async () => {
      try {
        if (!props.activeTable.value) return;
        isLoadingOrders.value = true;
        const table = props.tables.find(
          (el) => el.id === props.activeTable.value?.tableId,
        );
        if (!table) return;
        const orderIds = table.orders.map((order) => order.id);
        if (orderIds.length > 0) {
          console.log("orderIds", orderIds);
          const orders = await api.getOrders(orderIds);
          tableOrders.value = orders;
        }
      } catch (error) {
        console.error("Failed to fetch table orders:", error);
      } finally {
        isLoadingOrders.value = false;
      }
    };
    onMounted(() => {
      fetchTableOrders();
    });
    const { t } = useLocalization();
    const tabs = [
      {
        value: "overview",
        label: "Overview",
      },
      {
        value: "payments",
        label: "Payments",
      },
      ...(props.isEditable
        ? [
            {
              value: "settings",
              label: <span class="material-symbols-outlined">settings</span>,
            },
          ]
        : []),
    ];
    const activeTab = ref<string>("overview");
    const isNotesModalOpen = ref(false);
    const currentNotes = ref("");
    const toast = useToast();
    const trpc = useTrpc();
    const isTogglingStatus = ref(false);
    const toggleBusyMutation = useMutation({
      mutationFn: (input: { table_id: number; current_status: number }) =>
        trpc.tables.toggleBusy.mutate(input),
    });
    onMounted(() => {
      if (tableStatus?.party?.name) {
        currentNotes.value = tableStatus.party?.name;
      } else {
        currentNotes.value = "";
      }
    });

    const handleToggleTableStatus = async () => {
      window.alert("Feature disabled");
      return;

      const currentTableStatus = tableStatus!;
      if (!currentTableStatus) return;
      try {
        isTogglingStatus.value = true;
        const currentStatus = currentTableStatus.table.is_busy ? 1 : 0;
        await toggleBusyMutation.mutateAsync({
          table_id: currentTableStatus.table.id,
          current_status: currentStatus,
        });
        toast.showToast(
          currentStatus === 1 ? "Table marked as free" : "Table marked as busy",
          {
            type: "success",
            autoHide: 3000,
            icon: "table_restaurant",
          },
        );
        props.onPayment?.();
      } catch (error) {
        console.error("Failed to toggle table status:", error);
        toast.showToast("Failed to update table status", {
          type: "error",
          autoHide: 3000,
          icon: "error",
        });
      } finally {
        isTogglingStatus.value = false;
      }
    };

    return () => {
      console.log("Rendering table");

      if (!tableStatus) return;

      return (
        <RightSheet
          isOpen={true}
          title={
            <div class="flex flex-1 justify-center items-center pr-3">
              <div class="flex flex-1 flex-col">
                <div class="flex-1 text-2xl text-accent">
                  {tableStatus.table.name}
                </div>
                <div
                  onClick={() => (isNotesModalOpen.value = true)}
                  class="flex justify-start items-center"
                >
                  {currentNotes.value ? (
                    <div class="flex gap-2 items-center">
                      <span class="material-symbols-outlined text-2xl">
                        notes
                      </span>
                      {currentNotes.value}
                    </div>
                  ) : null}
                  <span class="material-symbols-outlined text-2xl max-w-24">
                    edit_square
                  </span>
                </div>
              </div>
              <div class="flex-1 flex justify-end items-end">
                <SegmentSelect
                  options={tabs}
                  value={activeTab.value}
                  onChange={(v) => {
                    activeTab.value = v;
                  }}
                />
                <div class="dropdown dropdown-bottom dropdown-end flex items-center">
                  <div tabindex="0" role="button" class="">
                    <span class="material-symbols-outlined text-4xl">
                      more_vert
                    </span>
                  </div>
                  <ul
                    tabindex="0"
                    class="dropdown-content text-accent menu bg-base-200 border border-base-300 rounded z-[10] w-52 p-0 shadow"
                  >
                    <li>
                      <a
                        class="py-6 text-1xl"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          console.log("changing table");
                          showChangeTableModal.value = true;
                        }}
                      >
                        {t("tables.general.change_table")}
                      </a>
                    </li>
                    <li>
                      <a
                        class="py-6 text-1xl"
                        href="#"
                        onClick={(e) => {
                          //e.preventDefault();
                          showReassignItemsModal.value = true;
                        }}
                      >
                        Reassign Items
                      </a>
                    </li>
                    <li>
                      <a
                        class="py-6 text-1xl"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleToggleTableStatus();
                          (e.currentTarget as HTMLAnchorElement).blur();
                        }}
                      >
                        {isTogglingStatus.value ? (
                          <span class="loading loading-spinner loading-sm"></span>
                        ) : tableStatus.table.is_busy ? (
                          "Mark as Free"
                        ) : (
                          "Mark as Busy"
                        )}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          }
          onClose={() => {
            console.log("closing table sheet");
            props.activeTable.value = undefined;
          }}
          style={""}
        >
          <div class="flex flex-col flex-1 overflow-hidden">
            <TableToolbar
              tableStatus={tableStatus}
              onPayment={() => {
                props.onPayment ? props.onPayment() : null;
                props.activeTable.value = undefined;
              }}
            />
            <div class="flex-1 flex flex-col overflow-y-auto">
              {(function () {
                if (activeTab.value === "overview") {
                  return <TableOverview tableStatus={tableStatus} />;
                } else if (activeTab.value === "settings") {
                  return (
                    <TableSettings
                      tableStatus={tableStatus}
                      floorPlan={props.floorPlan}
                      activeTable={props.activeTable}
                      planConfig={props.activeTable.value}
                      onFloorPlanChange={props.onFloorPlanChange}
                      tables={props.tables}
                      isEditable={props.isEditable}
                    />
                  );
                } else if (activeTab.value === "payments") {
                  return <TablePaymentsList tableStatus={tableStatus} />;
                }
              })()}
            </div>
          </div>
          {isNotesModalOpen.value && (
            <TableNotesModal
              onClose={() => {
                isNotesModalOpen.value = false;
              }}
              onSuccess={(text) => {
                currentNotes.value = text || "";
                isNotesModalOpen.value = false;
                props.onPayment && props.onPayment();
              }}
              tableStatus={tableStatus}
            />
          )}
          {showChangeTableModal.value && (
            <ChangeTableModal
              currentTable={tableStatus}
              onClose={() => (showChangeTableModal.value = false)}
              onSuccess={() => {
                showChangeTableModal.value = false;
                toast.showToast("Table changed successfully", {
                  type: "success",
                  autoHide: 3000,
                  icon: "table_restaurant",
                });
                props.onPayment && props.onPayment();
              }}
            />
          )}
          {showReassignItemsModal.value && (
            <TransferItemsToTableModal
              currentTable={tableStatus}
              onClose={() => (showReassignItemsModal.value = false)}
              onSuccess={() => {
                showReassignItemsModal.value = false;
                props.onPayment && props.onPayment();
              }}
            />
          )}
        </RightSheet>
      );
    };
  },
});
